import React, { useEffect, useState } from 'react';
import { Loader2, ArrowRight, Copy, Check, Tag } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackGenerateLead } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#C7FA3E';

export const ThreeForSeventyFive = () => {
  const [tees, setTees] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#three-for-75') {
      setTimeout(() => document.getElementById('three-for-75')?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  }, [location.hash]);

  useEffect(() => {
    axios.get(`${API}/api/products?limit=100`).then(({ data }) => {
      const t = (data || []).filter((p) => p.category === 'T-Shirts' && p.images?.length);
      setTees(t.slice(0, 3));
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Email is required'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/promo/three-for-75`, { email });
      setCode(data.code || '');
      if (data.code) localStorage.setItem('ninja_triple_code', data.code);
      trackGenerateLead('three_for_75');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="three-for-75" className="section-spacing relative overflow-hidden" data-testid="three-for-75-section">
      <div className="absolute inset-0 ninja-glow opacity-40" />
      <div className="container-ninja relative z-10">
        <div className="border border-zinc-800 bg-zinc-950/80 backdrop-blur">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left — the offer */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-4" style={{ color: LIME, fontFamily: 'Oswald, sans-serif' }}>
                <Tag className="w-3.5 h-3.5" /> Merch Deal · Limited Codes
              </p>
              <h2 className="text-white text-4xl sm:text-5xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
                Any 3 Tees. <span style={{ color: LIME }}>$75.</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-2">
                Stack any three NinjAthlete tees — normally <span className="text-zinc-200 line-through">$105+</span> — and your
                one-time code knocks the bundle to about <span className="font-bold" style={{ color: LIME }}>$75</span>.
              </p>
              <p className="text-zinc-500 text-sm mb-8">Drop your email and the code is yours. One per athlete.</p>

              {code ? (
                <div data-testid="three75-success">
                  <p className="text-zinc-400 text-sm mb-2">Your one-time code — we emailed you a copy:</p>
                  <div className="flex items-stretch gap-2 mb-4">
                    <span
                      className="flex-1 px-5 py-3.5 text-2xl text-center border border-dashed"
                      style={{ color: LIME, borderColor: `${LIME}66`, backgroundColor: `${LIME}0d`, fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.12em' }}
                      data-testid="three75-code"
                    >
                      {code}
                    </span>
                    <button
                      onClick={copyCode}
                      className="px-4 border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors"
                      data-testid="three75-copy-btn"
                      aria-label="Copy code"
                    >
                      {copied ? <Check className="w-4 h-4" style={{ color: LIME }} /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm uppercase tracking-widest font-bold transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: LIME, color: '#0B0B0C', fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="three75-shop-link"
                  >
                    Stack Your 3 Tees <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="three75-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="input-ninja flex-1 px-4 py-3.5 text-sm text-white placeholder-zinc-600"
                    data-testid="three75-email-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm uppercase tracking-widest font-bold whitespace-nowrap transition-transform hover:scale-[1.02] disabled:opacity-60"
                    style={{ backgroundColor: LIME, color: '#0B0B0C', fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="three75-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Unlock the Code <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}
              {error && <p className="text-red-400 text-sm mt-3" data-testid="three75-error">{error}</p>}
              {!code && <p className="text-zinc-600 text-xs mt-4">One-time use · works on all tees · we respect your inbox.</p>}
            </div>

            {/* Right — the tees */}
            <div className="border-t lg:border-t-0 lg:border-l border-zinc-800 p-8 md:p-12 flex flex-col justify-center" data-testid="three75-tees">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {tees.map((t, i) => (
                  <Link key={t.slug} to={`/shop/${t.slug}`} className="group" data-testid={`three75-tee-${i}`}>
                    <div className="aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-[#C7FA3E]/50 transition-colors">
                      <img
                        src={t.images[0]?.startsWith('/') ? `${API}${t.images[0]}` : t.images[0]}
                        alt={t.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <p className="text-zinc-400 text-[11px] mt-2 leading-tight truncate">{t.name}</p>
                    <p className="text-zinc-600 text-[11px] line-through">${Math.round(t.price)}</p>
                  </Link>
                ))}
              </div>
              <div className="flex items-baseline gap-3">
                <p className="text-white text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  = <span style={{ color: LIME }}>$75</span>
                </p>
                <p className="text-zinc-500 text-xs uppercase tracking-wider">for all three with your code</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThreeForSeventyFive;
